import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  BookOpen,
  Check,
  ChevronDown,
  FileText,
  MessageSquareText,
  Pointer,
  X,
} from 'lucide-react';
import { cn } from '../../lib/cn';
import { EsqueletoLineas } from '../ui/Esqueleto';
import { indicePorBloque, type Bloque, type ResumenBloques } from '../../lib/documentos';

/**
 * VISOR DE DOCUMENTO — el párrafo como unidad direccionable.
 *
 * Hasta hoy el validador comentaba el documento entero: veinticinco comentarios
 * sobre cincuenta páginas, y después alguien adivinaba a qué párrafo se refería
 * cada uno. Acá se toca el párrafo y el comentario queda colgado de él.
 *
 * Dos decisiones sostienen que esto funcione en un teléfono (SSOT §6.1):
 *
 * 1. TAP SIMPLE = PÁRRAFO COMPLETO. La selección nativa de texto dentro de un
 *    documento con scroll es un desastre en móvil: se arrastra, se pierde, se
 *    corrige. El párrafo además es la unidad natural de anclaje — nadie dice
 *    «desde la palabra 14 hasta la 27», dice «lo del RF-041».
 * 2. INTERRUPTOR LECTURA / SEÑALAR. En lectura el texto es texto: se desliza y
 *    se copia sin que ningún toque cambie nada. En señalar, cada párrafo es un
 *    botón. Así el scroll no pelea con la selección.
 *
 * El componente es de SOLO LECTURA sobre el documento y CONTROLADO en la
 * selección: recibe los bloques y el bloque elegido por props, y avisa hacia
 * arriba. Qué se hace con esa selección —grabar una nota de voz, escribir,
 * anclar el aporte— lo decide la página, no el visor.
 */

export type ModoVisor = 'lectura' | 'anotacion';

interface VisorDocumentoProps {
  /** Bloques de la versión que se está validando, en su orden real. */
  bloques: Bloque[];
  /** `id_estable` del párrafo elegido, o null. Controlado por la página. */
  seleccionado: string | null;
  /** Avisa la selección hacia arriba. Vuelve a null cuando se suelta. */
  onSeleccionar: (idEstable: string | null) => void;
  /** Conteos por párrafo (SQL determinista del backend). Opcional. */
  resumen?: ResumenBloques | null;
  /** Nombre del documento. Si la página ya lo muestra, no lo pases. */
  titulo?: string | null;
  cargando?: boolean;
  /** Validación cerrada o vista de consulta: se lee, no se señala. */
  soloLectura?: boolean;
  /**
   * Recordatorio del párrafo elegido dentro de la cabecera pegajosa. Ponelo en
   * false si la página ya muestra su propia tira de selección: dos avisos de lo
   * mismo en la misma pantalla es ruido, no refuerzo.
   */
  mostrarSeleccion?: boolean;
  /** Modo controlado desde afuera. Si no va, el visor lo maneja solo. */
  modo?: ModoVisor;
  onModo?: (modo: ModoVisor) => void;
}

/** Cuántos párrafos se pintan de golpe. Un documento largo no arranca entero. */
const TRAMO = 120;

/** Un título se ve como título y respeta su nivel. Mapas fijos: sin clases armadas al vuelo. */
const CLASES_TITULO: Record<number, string> = {
  1: 'font-display text-lg font-semibold leading-snug text-content',
  2: 'font-display text-base font-semibold leading-snug text-content',
  3: 'font-sans text-sm font-semibold leading-snug text-content',
};
const SANGRIA_TITULO: Record<number, string> = { 1: '', 2: '', 3: 'pl-2', 4: 'pl-4' };

function nivelSeguro(nivel: number | null): number {
  if (!nivel || nivel < 1) return 1;
  return nivel > 4 ? 4 : nivel;
}

export function VisorDocumento({
  bloques,
  seleccionado,
  onSeleccionar,
  resumen,
  titulo,
  cargando = false,
  soloLectura = false,
  mostrarSeleccion = true,
  modo: modoProp,
  onModo,
}: VisorDocumentoProps) {
  // El modo puede venir de afuera; si no, vive acá. Cerrada la validación,
  // solo se lee: ofrecer «señalar» sería ofrecer algo que ya no se puede hacer.
  const [modoInterno, setModoInterno] = useState<ModoVisor>('anotacion');
  const modo: ModoVisor = soloLectura ? 'lectura' : modoProp ?? modoInterno;
  const anotando = modo === 'anotacion';

  const cambiarModo = (m: ModoVisor) => {
    setModoInterno(m);
    onModo?.(m);
  };

  const conteos = useMemo(() => indicePorBloque(resumen), [resumen]);

  // Render progresivo: un documento de miles de párrafos no se le sirve entero
  // a un teléfono. Se abre por tramos y se abre solo hasta donde haga falta.
  const [tope, setTope] = useState(TRAMO);
  const indiceSel = useMemo(
    () => (seleccionado ? bloques.findIndex((b) => b.id_estable === seleccionado) : -1),
    [bloques, seleccionado],
  );
  useEffect(() => {
    if (indiceSel >= tope) setTope(Math.ceil((indiceSel + 1) / TRAMO) * TRAMO);
  }, [indiceSel, tope]);

  // Si la selección llega de afuera (la página apunta a un párrafo desde un
  // comentario), lo traemos a la vista. Si el toque fue acá, no se mueve nada:
  // saltar bajo el dedo de quien acaba de tocar es desorientador.
  const nodos = useRef(new Map<string, HTMLLIElement>());
  const tocadoAca = useRef(false);
  const yaTraido = useRef<string | null>(null);
  useEffect(() => {
    if (!seleccionado) {
      yaTraido.current = null;
      return;
    }
    if (yaTraido.current === seleccionado) return; // abrir otro tramo no re-centra
    if (tocadoAca.current) {
      tocadoAca.current = false;
      yaTraido.current = seleccionado;
      return;
    }
    const el = nodos.current.get(seleccionado);
    if (!el) return; // fuera del tramo abierto: el efecto vuelve al ampliarse `tope`
    yaTraido.current = seleccionado;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [seleccionado, tope]);

  const elegir = (id: string) => {
    tocadoAca.current = true;
    onSeleccionar(seleccionado === id ? null : id);
  };

  const visibles = bloques.slice(0, tope);
  const restantes = bloques.length - visibles.length;
  const bloqueSel = indiceSel >= 0 ? bloques[indiceSel] : null;

  if (cargando) {
    return (
      <section className="rounded-xl border border-border bg-surface-raised p-4 shadow-sm">
        <EsqueletoLineas lineas={8} />
      </section>
    );
  }

  if (bloques.length === 0) {
    return (
      <section className="rounded-xl border border-dashed border-border bg-surface-raised p-5 text-center">
        <FileText className="mx-auto h-6 w-6 text-content-muted" aria-hidden="true" />
        <p className="mt-2 font-sans text-sm text-content-muted">
          Este documento todavía no tiene párrafos para mostrar. Podés dejar tu
          comentario igual: queda sobre el documento completo.
        </p>
      </section>
    );
  }

  return (
    // Sin `overflow-hidden` acá: un ancestro con overflow oculto convierte a la
    // cabecera pegajosa en una cabecera quieta. Las esquinas las redondea cada
    // parte por su cuenta.
    <section className="rounded-xl border border-border bg-surface-raised shadow-sm">
      {/* Cabecera pegajosa: el modo y el párrafo elegido siguen a la vista
          mientras se recorre el documento.

          Es una CAPA, no una franja: el texto pasa por debajo desenfocándose
          en vez de cortarse contra un bloque opaco. En un documento de
          seiscientos párrafos esa diferencia importa — se ve que hay más
          arriba, y la barra deja de sentirse como un pedazo de pantalla
          perdido. El desenfoque se desactiva solo si el sistema pide menos
          transparencia (regla en la capa base). */}
      <div className="sticky top-0 z-10 rounded-t-xl border-b border-border bg-surface-velada/80 px-3 py-3 backdrop-blur-md">
        {titulo && (
          <p className="mb-2 flex items-center gap-2 font-display text-sm font-semibold text-content">
            <FileText className="h-4 w-4 shrink-0 text-content-muted" aria-hidden="true" />
            <span className="min-w-0 truncate">{titulo}</span>
          </p>
        )}

        {!soloLectura && (
          <div
            role="group"
            aria-label="Qué hace tu toque sobre el documento"
            className="inline-flex gap-1 rounded-lg border border-border-strong bg-surface-base p-1"
          >
            <BotonModo
              activo={!anotando}
              onClick={() => cambiarModo('lectura')}
              icono={<BookOpen className="h-4 w-4" aria-hidden="true" />}
              etiqueta="Leer"
            />
            <BotonModo
              activo={anotando}
              onClick={() => cambiarModo('anotacion')}
              icono={<Pointer className="h-4 w-4" aria-hidden="true" />}
              etiqueta="Señalar párrafo"
            />
          </div>
        )}

        <p className="mt-2 font-sans text-xs leading-relaxed text-content-muted" aria-live="polite">
          {soloLectura
            ? 'Esta validación ya cerró: el documento queda para consulta.'
            : anotando
              ? 'Tocá el párrafo del que vas a hablar. Tocalo otra vez para soltarlo.'
              : 'Deslizá para leer con calma. Cuando quieras comentar uno, pasá a «Señalar párrafo».'}
        </p>

        {bloqueSel && mostrarSeleccion && (
          <div className="mt-2 flex items-start gap-2 rounded-lg border border-primary bg-primary/10 px-3 py-2">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
            <p className="min-w-0 flex-1 font-sans text-xs leading-relaxed text-content">
              <span className="font-semibold">
                {bloqueSel.ref ? `${bloqueSel.ref} · ` : 'Tu comentario va a este párrafo: '}
              </span>
              <span className="line-clamp-2">{bloqueSel.texto}</span>
            </p>
            <button
              type="button"
              onClick={() => onSeleccionar(null)}
              aria-label="Soltar el párrafo elegido"
              className="-my-1 -mr-1 flex min-h-touch min-w-touch items-center justify-center rounded-md text-content-muted transition-colors duration-fast hover:text-content focus-visible:outline-none focus-visible:shadow-focus"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>

      {/* El documento */}
      <ol
        className="flex flex-col gap-1 px-2 py-3"
        role={anotando ? 'radiogroup' : undefined}
        aria-label={anotando ? 'Párrafos del documento' : undefined}
      >
        {visibles.map((b) => (
          <FilaBloque
            key={b.id_estable}
            bloque={b}
            activo={b.id_estable === seleccionado}
            anotando={anotando}
            comentarios={conteos.get(b.id_estable)?.comentarios ?? 0}
            onElegir={elegir}
            registrar={(el) => {
              if (el) nodos.current.set(b.id_estable, el);
              else nodos.current.delete(b.id_estable);
            }}
          />
        ))}
      </ol>

      {restantes > 0 && (
        <div className="px-3 pb-3">
          <button
            type="button"
            onClick={() => setTope((t) => t + TRAMO)}
            className="flex min-h-touch w-full items-center justify-center gap-2 rounded-lg border border-border-strong bg-surface-base px-3 font-sans text-sm font-medium text-content transition-colors duration-fast hover:border-primary focus-visible:outline-none focus-visible:shadow-focus"
          >
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
            Mostrar más del documento ({restantes}{' '}
            {restantes === 1 ? 'párrafo restante' : 'párrafos restantes'})
          </button>
        </div>
      )}

      {resumen && (
        <p className="border-t border-border px-3 py-2.5 font-sans text-xs leading-relaxed text-content-muted">
          {resumen.bloques_con_comentario} de {resumen.bloques_total}{' '}
          {resumen.bloques_total === 1 ? 'párrafo tiene' : 'párrafos tienen'} comentarios
          {resumen.comentarios_sin_anclar > 0 && (
            <>
              {' · '}
              {resumen.comentarios_sin_anclar}{' '}
              {resumen.comentarios_sin_anclar === 1
                ? 'comentario quedó sobre el documento completo'
                : 'comentarios quedaron sobre el documento completo'}
            </>
          )}
        </p>
      )}
    </section>
  );
}

/** Una mitad del interruptor. Target táctil completo, nunca un texto suelto. */
function BotonModo({
  activo,
  onClick,
  icono,
  etiqueta,
}: {
  activo: boolean;
  onClick: () => void;
  icono: ReactNode;
  etiqueta: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={activo}
      onClick={onClick}
      className={cn(
        'inline-flex min-h-touch items-center gap-1.5 rounded-md px-3',
        'font-sans text-sm font-medium transition-colors duration-fast',
        'focus-visible:outline-none focus-visible:shadow-focus',
        activo
          ? 'bg-primary text-content-on-primary'
          : 'text-content-muted hover:text-content',
      )}
    >
      {icono}
      {etiqueta}
    </button>
  );
}

/**
 * Un párrafo. En modo señalar es un botón de radio (uno elegido por vez); en
 * modo lectura es texto plano, para que se pueda seleccionar y copiar como en
 * cualquier documento.
 */
function FilaBloque({
  bloque,
  activo,
  anotando,
  comentarios,
  onElegir,
  registrar,
}: {
  bloque: Bloque;
  activo: boolean;
  anotando: boolean;
  comentarios: number;
  onElegir: (id: string) => void;
  registrar: (el: HTMLLIElement | null) => void;
}) {
  const esTitulo = bloque.tipo === 'titulo';
  const nivel = nivelSeguro(bloque.nivel);

  const clasesTexto = esTitulo
    ? cn(CLASES_TITULO[nivel] ?? CLASES_TITULO[3], SANGRIA_TITULO[nivel])
    : bloque.tipo === 'fila'
      ? 'border-l-2 border-border pl-3 font-sans text-sm leading-relaxed text-content'
      : 'font-sans text-base leading-relaxed text-content';

  const cuerpo = (
    <>
      {(bloque.ref || comentarios > 0) && (
        <span className="mb-1 flex items-center justify-between gap-2">
          {bloque.ref ? (
            <span className="rounded-md border border-border bg-surface-sunken px-1.5 py-0.5 font-sans text-xs font-semibold tracking-wide text-content-muted">
              {bloque.ref}
            </span>
          ) : (
            <span aria-hidden="true" />
          )}
          {comentarios > 0 && (
            <span
              className="inline-flex shrink-0 items-center gap-1 rounded-full border border-border bg-surface-sunken px-2 py-0.5 font-sans text-xs font-medium text-content-muted"
              aria-label={comentarios === 1 ? '1 comentario' : `${comentarios} comentarios`}
            >
              <MessageSquareText className="h-3 w-3" aria-hidden="true" />
              {comentarios}
            </span>
          )}
        </span>
      )}
      <span className={cn('block', clasesTexto)}>
        {bloque.tipo === 'lista' && <span aria-hidden="true">· </span>}
        {bloque.texto}
      </span>
    </>
  );

  // Sin cambio de grosor entre estados: el borde transparente reserva su sitio
  // y el párrafo no salta 2px cuando se elige.
  const base = 'block w-full rounded-lg border-2 px-3 py-2.5 text-left transition-colors duration-fast';

  return (
    // `presentation` cuando el grupo es un radiogroup: un `listitem` entre el
    // grupo y sus radios rompe la relación para el lector de pantalla.
    <li ref={registrar} role={anotando ? 'presentation' : undefined} className="scroll-mt-32">
      {anotando ? (
        <button
          type="button"
          role="radio"
          aria-checked={activo}
          onClick={() => onElegir(bloque.id_estable)}
          className={cn(
            base,
            'focus-visible:outline-none focus-visible:shadow-focus',
            activo
              ? 'border-primary bg-primary/10'
              : 'border-transparent hover:border-border-strong',
          )}
        >
          {cuerpo}
        </button>
      ) : (
        <div
          role={esTitulo ? 'heading' : undefined}
          aria-level={esTitulo ? nivel : undefined}
          className={cn(base, activo ? 'border-primary bg-primary/10' : 'border-transparent')}
        >
          {cuerpo}
        </div>
      )}
    </li>
  );
}
